import { gitOriginFromUrl, OAUTH_PROVIDERS, oauthService, Project, TypeId, type GitOrigin } from '@sdk';
import { AutoLoginField } from '@src/pages/hub-home/AutoLoginField';
import { LoginAccessField } from '@src/pages/hub-home/LoginAccessField';
import { Button } from '@src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@src/components/ui/dialog';
import { Input } from '@src/components/ui/input';
import { ProjectSelector } from '@src/components/project-selector/ProjectSelector';
import { projectEntitiesToSelectorItems } from '@src/components/project-selector/project-items';
import { cn } from '@src/lib/utils';
import { GitRepoPicker, type GitTarget } from '@src/components/git/GitRepoPicker';
import { ComputeNodeSize, type ComputeNode } from '@sdk';
import {
  AGENT_MACHINE_SIZE_LABELS,
  AGENT_MACHINE_SIZES,
} from '@src/components/assets/editor/agent-profile/agent-vocabularies';
import {
  DEFAULT_LOGIN_ACCESS,
  type ContextProject,
  type LoginAccess,
  type SandboxSetup,
  type Step,
} from '@src/hooks/use-sandboxes';
import { StepList } from '@src/components/ui/step-list';
import { Briefcase, GitBranch, Loader2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

/**
 * A picked source, normalised at pick time.
 *
 * `gitOrigin` is null for a project that was never cloned from anywhere — it
 * can still be the project the sandbox loads (the box mounts it empty), but it
 * has nothing to clone as an asset. `projectId` is set when the user picked an
 * existing project rather than pasting a URL, and is what the box adopts so one
 * id spans hub and sandbox.
 */
interface Source {
  name: string;
  gitOrigin: GitOrigin | null;
  projectId?: string;
}

/** Which chip's panel is open, if any. */
type Panel = 'project' | 'git';

/** Which field owns the one open panel. */
interface OpenPanel {
  field: string;
  panel: Panel;
}

function sourceFromProject(project: Project): Source {
  return { name: project.displayName, gitOrigin: project.origin ?? null, projectId: project.id };
}

interface NewSandboxDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultName: string;
  /** Pre-fill the project to load from a repo URL (e.g. a ?setup_git= deep link). */
  initialGitUrl?: string;
  /** The project the user is working on — loaded unless they change it. */
  currentProject?: Project | null;
  /** Everything they could load instead, and everything they can add as an asset. */
  projects?: Project[];
  /**
   * Write the box down. Resolves with the created node, or rejects.
   *
   * Provisions NOTHING — no VM, no cost, nothing to open. Deliberately NOT
   * fire-and-forget: the dialog stays open across the call so it can report what
   * happened, which means it has to be able to await the outcome.
   */
  onCreate: (opts: {
    name: string;
    size: ComputeNodeSize;
    sandboxProject?: SandboxSetup;
  }) => Promise<ComputeNode | null>;
  /**
   * Boot the created box and set it up. Resolves when it is ready, or rejects.
   *
   * This is where the minute of work lives, and it is why the dialog has a
   * fourth state: the progress has to be visible somewhere, and behind a closed
   * dialog is not somewhere.
   */
  onLaunch: (node: ComputeNode, opts: { autoLogin: boolean; loginAccess: LoginAccess }) => Promise<ComputeNode | null>;
  /** Open a launched box. Called from the dialog's own Open click, so the tab
   *  is claimed inside a real user gesture. */
  onOpen: (node: ComputeNode) => void;
  /** Live progress rows for the launch in flight. */
  steps: Step[];
}

/**
 * Start a sandbox: name it, say which project it loads, and add any asset
 * packages — help desks or skills repos that get cloned in, indexed, and
 * added as dependencies of that project.
 *
 * Five states, and the dialog stays open across all of them:
 *
 *   idle      Cancel | Create
 *   creating  one save, so this is a blink
 *   created   auto-login tick, then Done | Launch — nothing is running yet
 *   launching the progress checklist, inline
 *   launched  Done | Open sandbox
 *
 * Create writes the box down and stops. Launch is what boots it, which is the
 * whole point of the split: a box you make now and open on Thursday costs
 * nothing in between. Done is available in both settled states and means the
 * same thing each time — "not now" — never "undo".
 *
 * The auto-login tick sits by Launch and not in the share dialog because it
 * governs what THIS open does: whether bringing the workspace up signs a person
 * into it. Handing the box over is the share dialog's business and stayed there.
 *
 * It used to close on click and fire the launch at the page behind it, which
 * put the progress rows and any failure somewhere the user was no longer
 * looking — a create that failed simply appeared to do nothing. Staying open is
 * what makes the outcome visible. The open is still its own click (now "Open
 * sandbox", after the pipeline, rather than "Launch" before it), so there is
 * still no placeholder tab to claim up front and none to close on error.
 *
 * Both fields are the same control ({@link SourceField}): the picks as
 * removable pills, then a chip per way in — pick a project, or a git repo
 * (the shared `GitRepoPicker`: paste a URL or browse GitHub, with its branch) —
 * each opening its own panel directly underneath. The only
 * difference is what a pick does (replace vs. append) and which projects are
 * on offer.
 *
 * Only ONE panel is open across the whole dialog, which is why the open-panel
 * state lives here rather than in each field: the git panel carries the repo
 * table (and, unconnected, the Connect button), and two of those stacked would
 * both bury the footer and offer the same connection twice.
 *
 * It deliberately does NOT probe repo access first: that probe
 * (`/api/v1/git/remote-access`) is a flow_sdk route the hub does not register,
 * so it 404s for every repo and asked people to connect GitHub for public ones.
 * The clone step reports the real failure.
 */
export function NewSandboxDialog({
  open,
  onOpenChange,
  defaultName,
  initialGitUrl,
  currentProject,
  projects,
  onCreate,
  onLaunch,
  onOpen,
  steps,
}: NewSandboxDialogProps) {
  const { t } = useLingui();
  const [name, setName] = useState(defaultName);
  // The hub's own default size. Fixed at create: the box is provisioned at it.
  const [size, setSize] = useState<ComputeNodeSize>(ComputeNodeSize.LARGE);
  // Once picked, the size list folds to the chosen row so the dialog's footer stays in view.
  const [sizesOpen, setSizesOpen] = useState(true);
  const [loadedProject, setLoadedProject] = useState<Source | null>(null);
  const [assets, setAssets] = useState<Source[]>([]);
  const [openPanel, setOpenPanel] = useState<OpenPanel | null>(null);
  // idle -> creating -> created -> launching -> launched. The dialog stays
  // mounted across all five, so a failure lands back on the last settled state
  // with the error visible rather than behind a dialog that already closed.
  const [phase, setPhase] = useState<'idle' | 'creating' | 'created' | 'launching' | 'launched'>('idle');
  const [created, setCreated] = useState<ComputeNode | null>(null);
  // The hub's own default. Unticking it before the first launch is the only way
  // to get a box that never signs anyone in — after the launch the session
  // already exists, and turning it off then is a sign-out, not a setting.
  const [autoLogin, setAutoLogin] = useState(true);
  const [loginAccess, setLoginAccess] = useState<LoginAccess>(DEFAULT_LOGIN_ACCESS);
  const [error, setError] = useState('');

  // Reset on OPEN only. `defaultName` changes whenever the sandbox list refetches
  // (it's derived from it), so depending on it here would wipe what the user has
  // already typed mid-dialog.
  useEffect(() => {
    if (!open) return;
    setName(defaultName);
    setSize(ComputeNodeSize.LARGE);
    setSizesOpen(true);
    const fromUrl = initialGitUrl ? gitOriginFromUrl(initialGitUrl) : null;
    setLoadedProject(
      fromUrl ? { name: fromUrl.name, gitOrigin: fromUrl } : currentProject ? sourceFromProject(currentProject) : null,
    );
    setAssets([]);
    setOpenPanel(null);
    setError('');
    setAutoLogin(true);
    setLoginAccess(DEFAULT_LOGIN_ACCESS);
    // Reopening starts a NEW sandbox. Without this the dialog would come back up
    // on the `created` phase, showing Done/Launch for the box made last time.
    setPhase('idle');
    setCreated(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // An asset has to be cloned, so a project with no repository behind it can't
  // be one — it would attach an empty folder with nothing to index.
  const assetCandidates = useMemo(() => (projects ?? []).filter((p) => !!p.origin), [projects]);

  const onInvalidUrl = useCallback(() => setError(t`Enter a valid GitHub repository URL.`), [t]);

  /**
   * Let whoever OPENS this box clone its repo.
   *
   * A sandbox travels; a personal GitHub connection does not. Share or hand a box
   * over and the recipient launches it as themselves, so the hub resolves THEIR
   * GitHub — which they have most likely never connected — and the clone runs
   * anonymously. A private repo then dies on `fatal: could not read Username`,
   * which reaches them as "ask whoever shared it to start it": a machine problem
   * named for what is really "you are not the person who connected GitHub".
   *
   * The hub will lend YOUR credential for exactly this repo, but only if it was
   * attached to the project — and nothing granted that, so the only way to satisfy
   * it was a hand-written API call. This is the grant, made where the intent is:
   * you are choosing this project, for a box, right now.
   *
   * Attached to the PROJECT rather than the box, because that is what the consent
   * outlives — every sandbox built on this project reuses one grant instead of
   * needing a fresh one each time. Revoked from the same place as any other
   * attachment, and it lends nothing at all beyond this project's own repo: the
   * hub refuses any origin that is not the one recorded on the box.
   *
   * Best-effort by design. A box whose repo cannot be lent is still a working box
   * for its creator, so a failed attach must not fail the create — the person is
   * left exactly where they were, not staring at an error for something they never
   * asked for.
   */
  const lendRepoAccessToRecipients = useCallback(async (project: Source) => {
    // No repo, nothing to lend. A project with no git origin behind it needs no
    // credential to open, so asking for one would be noise.
    if (!project.projectId || !project.gitOrigin) return;
    try {
      await oauthService.attach(OAUTH_PROVIDERS.GITHUB, new TypeId(Project.type, project.projectId));
    } catch (e) {
      // Logged, not surfaced: see "best-effort" above.
      console.error('[NewSandboxDialog] could not attach GitHub to the project:', e);
    }
  }, []);

  const handleCreate = useCallback(async () => {
    // Guard the double click here as well as in the hook: the hook's ref stops a
    // second box being provisioned, but only disabling the button stops the
    // second click reading as "nothing happened".
    if (phase !== 'idle') return;
    const sandboxName = name.trim() || defaultName;
    // Asset packages are required dependencies: they travel with the project.
    const contextProjects: ContextProject[] = assets.flatMap((asset) =>
      asset.gitOrigin ? [{ gitOrigin: asset.gitOrigin, name: asset.name, optional: false }] : [],
    );
    const opts = loadedProject
      ? {
          name: sandboxName,
          size,
          sandboxProject: {
            name: loadedProject.name,
            ...(loadedProject.gitOrigin ? { gitOrigin: loadedProject.gitOrigin } : {}),
            ...(loadedProject.projectId ? { projectId: loadedProject.projectId } : {}),
            ...(contextProjects.length ? { contextProjects } : {}),
          },
        }
      : { name: sandboxName, size }; // nothing to load → a plain sandbox

    setPhase('creating');
    setError('');
    try {
      const node = await onCreate(opts);
      if (!node) {
        // The hook refused because a create was already in flight. Nothing was
        // provisioned, so this is not an error state to report — just go back.
        setPhase('idle');
        return;
      }
      setCreated(node);
      setPhase('created');
      // AFTER the box exists, and deliberately not awaited: the grant is for a
      // recipient who does not exist yet, so making the creator wait on it would
      // slow the one path that never needs it.
      if (loadedProject) void lendRepoAccessToRecipients(loadedProject);
    } catch (e) {
      // Back to idle, dialog still open, message on screen: the user can fix the
      // input and try again without rebuilding their picks.
      setError(e instanceof Error ? e.message : String(e));
      setPhase('idle');
    }
  }, [phase, name, size, defaultName, loadedProject, assets, onCreate, lendRepoAccessToRecipients]);

  const handleLaunch = useCallback(async () => {
    if (!created || phase !== 'created') return;
    setPhase('launching');
    setError('');
    try {
      const node = await onLaunch(created, { autoLogin, loginAccess });
      if (!node) {
        // The hook refused because a launch was already in flight. Nothing
        // booted, so this is not an error to report — just offer Launch again.
        setPhase('created');
        return;
      }
      setPhase('launched');
    } catch (e) {
      // Back to `created`, not `idle`: the box is written down either way, and
      // dropping to idle would offer to create a second one for the same picks.
      setError(e instanceof Error ? e.message : String(e));
      setPhase('created');
    }
  }, [created, phase, autoLogin, loginAccess, onLaunch]);

  const handleOpen = useCallback(() => {
    if (!created) return;
    // Inside the click gesture, with the final URL — the whole reason the
    // placeholder-tab workaround could be deleted. It could NOT be folded back
    // into the Launch click: a minute of pipeline sits between the two, and a
    // `window.open` that far from a gesture is what popup blockers eat.
    onOpen(created);
    onOpenChange(false);
  }, [created, onOpen, onOpenChange]);

  return (
    /* Closing is never conditional — a pending GitHub connect (which may never
       complete) spins its own button and nothing more. */
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            <Trans>New sandbox</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>Choose the project it loads, and any asset packages to bring with it.</Trans>
          </DialogDescription>
        </DialogHeader>

        <label className="mb-1 block text-xs font-medium text-muted-foreground">
          <Trans>Name</Trans>
        </label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={defaultName}
          className="mb-4 text-sm"
          autoFocus
        />

        <label className="mb-1 block text-xs font-medium text-muted-foreground">
          <Trans>Machine size</Trans>
        </label>
        <div className="mb-4 flex flex-col gap-1.5" role="radiogroup" aria-label={t`Machine size`}>
          {(sizesOpen ? AGENT_MACHINE_SIZES : [size]).map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={size === value}
              aria-expanded={sizesOpen}
              disabled={phase !== 'idle'}
              onClick={() => {
                if (!sizesOpen) return setSizesOpen(true);
                setSize(value);
                setSizesOpen(false);
              }}
              className={cn(
                'flex items-center justify-between rounded-md border px-3 py-2 text-start text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                size === value ? 'border-primary bg-primary/5' : 'hover:bg-muted/60',
              )}
              data-testid={`sandbox-size-${value}`}
            >
              {AGENT_MACHINE_SIZE_LABELS[value]}
              {!sizesOpen && (
                <span className="text-xs text-muted-foreground">
                  <Trans>Change</Trans>
                </span>
              )}
            </button>
          ))}
        </div>

        <SourceField
          label={t`Project to load`}
          testId="loaded-project"
          projects={projects ?? []}
          values={loadedProject ? [loadedProject] : []}
          onPick={setLoadedProject}
          onRemove={() => setLoadedProject(null)}
          emptyHint={t`Nothing selected — launches an empty sandbox.`}
          onInvalidUrl={onInvalidUrl}
          panel={openPanel?.field === 'loaded-project' ? openPanel.panel : null}
          onPanelChange={(panel) => setOpenPanel(panel ? { field: 'loaded-project', panel } : null)}
        />

        <SourceField
          label={t`Additional assets`}
          testId="assets"
          projects={assetCandidates}
          values={assets}
          onPick={(source) => setAssets((prev) => [...prev, source])}
          onRemove={(index) => setAssets((prev) => prev.filter((_, at) => at !== index))}
          emptyHint={t`Help desks or skills repos to load alongside it.`}
          onInvalidUrl={onInvalidUrl}
          panel={openPanel?.field === 'assets' ? openPanel.panel : null}
          onPanelChange={(panel) => setOpenPanel(panel ? { field: 'assets', panel } : null)}
        />

        {error && <p className="mt-2 text-xs text-destructive">{error}</p>}

        {/* What a created box is, before anyone launches it. Said plainly: the
            card on the page behind is the other half of this sentence. */}
        {phase === 'created' && (
          <p className="mt-2 text-xs text-muted-foreground" data-testid="sandbox-not-started">
            <Trans>Written down, not started. Launching boots it and sets the project up.</Trans>
          </p>
        )}

        {/* Progress lives INSIDE the dialog now. It used to render on the page
            behind a dialog that had already closed, which is where failures went
            to be missed. */}
        {(phase === 'launching' || phase === 'launched') && (
          <div className="mt-3" data-testid="sandbox-create-steps">
            <StepList steps={steps} />
          </div>
        )}

        {/* Auto-login can only be decided before the box boots; login access is
            set here too and can be changed later from the card. */}
        {phase === 'created' && (
          <>
            <AutoLoginField checked={autoLogin} onChange={setAutoLogin} testId="sandbox-auto-login" className="mt-3" />
            <LoginAccessField
              value={loginAccess}
              onChange={setLoginAccess}
              testId="sandbox-allow-login"
              className="mt-3"
            />
          </>
        )}

        <DialogFooter className="mt-4">
          {phase === 'launched' ? (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="done-sandbox">
                <Trans>Done</Trans>
              </Button>
              <Button onClick={handleOpen} data-testid="open-sandbox">
                <Trans>Open sandbox</Trans>
              </Button>
            </>
          ) : phase === 'created' || phase === 'launching' ? (
            <>
              {/* Done, not Cancel: the box exists either way. Closing here keeps
                  it — it is in the list on the page behind, ready to launch. */}
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={phase === 'launching'}
                data-testid="done-sandbox"
              >
                <Trans>Done</Trans>
              </Button>
              <Button onClick={() => void handleLaunch()} disabled={phase === 'launching'} data-testid="launch-sandbox">
                {phase === 'launching' && <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" />}
                <Trans>Launch</Trans>
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={phase === 'creating'}>
                <Trans>Cancel</Trans>
              </Button>
              {/* A half-finished connection doesn't block a create either: the
                  source is already chosen, and GitHub only ever mattered for
                  reaching a private repo. */}
              <Button onClick={() => void handleCreate()} disabled={phase === 'creating'} data-testid="create-sandbox">
                {phase === 'creating' && <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" />}
                <Trans>Create</Trans>
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One labelled field: what's picked so far, then the three ways to pick more.
 *
 * Used verbatim for the loaded project and for the asset packages — the caller
 * only decides whether a pick replaces or appends, and which projects are on
 * offer. The panel for a chip opens directly under the chip row, so the choice
 * and its consequence stay in one place; which panel is open is the caller's
 * state, because only one is open across the whole dialog.
 */
function SourceField({
  label,
  testId,
  projects,
  values,
  onPick,
  onRemove,
  emptyHint,
  onInvalidUrl,
  panel,
  onPanelChange,
}: {
  label: string;
  testId: string;
  projects: Project[];
  values: Source[];
  onPick: (source: Source) => void;
  onRemove: (index: number) => void;
  emptyHint: string;
  onInvalidUrl: () => void;
  panel: Panel | null;
  onPanelChange: (panel: Panel | null) => void;
}) {
  const { t } = useLingui();
  const [target, setTarget] = useState<GitTarget | null>(null);
  const togglePanel = (next: Panel) => onPanelChange(panel === next ? null : next);

  const pickedIds = useMemo(() => values.map((v) => v.projectId).filter((id): id is string => !!id), [values]);

  const pick = (source: Source) => {
    onPanelChange(null);
    setTarget(null);
    onPick(source);
  };

  const submitRepo = () => {
    if (target?.mode !== 'existing') return;
    const gitOrigin = gitOriginFromUrl(target.url);
    if (!gitOrigin) {
      onInvalidUrl();
      return;
    }
    pick({ name: gitOrigin.name, gitOrigin: target.branch ? { ...gitOrigin, branch: target.branch } : gitOrigin });
  };

  return (
    <div className="mb-4" data-testid={`${testId}-field`}>
      <label className="mb-1.5 block text-xs font-medium text-muted-foreground">{label}</label>

      {values.length > 0 ? (
        <div className="mb-1.5 flex flex-wrap gap-1.5" data-testid={`${testId}-values`}>
          {values.map((source, i) => (
            <span
              key={`${source.name}-${i}`}
              className="inline-flex max-w-full items-center gap-1 rounded-full border border-border bg-muted/50 py-0.5 pe-1 ps-2 text-xs"
            >
              {source.projectId ? (
                <Briefcase className="h-3 w-3 shrink-0 text-muted-foreground" />
              ) : (
                <GitBranch className="h-3 w-3 shrink-0 text-muted-foreground" />
              )}
              <span className="truncate">{source.name}</span>
              {!source.gitOrigin && (
                <span className="shrink-0 text-[10px] uppercase text-muted-foreground">
                  <Trans>no repo</Trans>
                </span>
              )}
              <button
                type="button"
                className="shrink-0 rounded-full p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                onClick={() => onRemove(i)}
                aria-label={t`Remove ${source.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className="mb-1.5 text-[11px] text-muted-foreground">{emptyHint}</p>
      )}

      {/* The three ways in, always all three. Hiding the project chip when the
          list happened to be empty (or fully picked) made the field look like
          git was the only way to name a source — on a fresh hub account, which
          has no projects yet, that is exactly when it misleads. The panel's own
          empty state says "no projects" far more honestly than a missing chip. */}
      <div className="flex flex-wrap items-center gap-1.5">
        <Chip
          active={panel === 'project'}
          onClick={() => togglePanel('project')}
          testId={`${testId}-chip-project`}
          label={t`Select project`}
          icon={<Briefcase className="h-3 w-3" />}
        />
        <Chip
          active={panel === 'git'}
          onClick={() => togglePanel('git')}
          testId={`${testId}-chip-git`}
          label={t`Git repo`}
          icon={<GitBranch className="h-3 w-3" />}
        />
      </div>

      {panel === 'project' && (
        <div className="mt-1.5 rounded-md border border-border p-1" data-testid={`${testId}-projects`}>
          <ProjectSelector
            projects={projectEntitiesToSelectorItems(projects)}
            selectedId={null}
            excludeIds={pickedIds}
            emptyMessage={t`No projects to choose from — use a git repo.`}
            onSelect={(id) => {
              const project = projects.find((p) => p.id === id);
              if (project) pick(sourceFromProject(project));
            }}
          />
        </div>
      )}

      {/* The shared picker: paste a URL (public repos need no connection) or
          browse GitHub — connecting, when needed, happens inside it and swaps
          to the repo table on its own. */}
      {panel === 'git' && (
        <div className="mt-1.5 flex flex-col gap-2 rounded-md border border-border p-2" data-testid={`${testId}-git`}>
          <GitRepoPicker
            modes={['existing']}
            onChange={setTarget}
            onEnter={submitRepo}
            urlPlaceholder="https://github.com/owner/repo"
            testIdPrefix={`${testId}-git`}
          />
          <Button
            size="sm"
            variant="outline"
            className="h-7 self-end px-2 text-xs"
            onClick={submitRepo}
            disabled={target?.mode !== 'existing'}
            data-testid={`${testId}-git-use`}
          >
            <Trans>Use</Trans>
          </Button>
        </div>
      )}
    </div>
  );
}

/** A small pill that opens its own panel underneath. */
function Chip({
  label,
  icon,
  active,
  onClick,
  testId,
}: {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors',
        active
          ? 'border-primary/50 bg-primary/10 text-foreground'
          : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
